<template>
  <div>
    <page-content-toolbar
      :projectName="`${project && project.name ? project.name : ' '}`"
      :title="$t('option.identifyContradictoryOptions')"
    />

    <v-container
      fluid
      class="px-12 pt-9 mb-12 pb-12"
    >
      <option-block
        @changeOption="changeCurrentOption($event)"
        :list-label="'Option'"
        :options="[currentOption]"
        :loading="loadingOptions"
        :option-list="optionList"
      />
      <option-block
        :list-label="$t('option.contradictory')"
        :options="computedContradictoryOptions"
        close
        :close-function="removeContradictoryOption"
        :loading="loadingConflictingOptions"
      />
      <template v-if="noMoreContradictoryOptions && !loadingOptions && !loadingConflictingOptions">
        <v-row
          no-gutters
          justify="start"
          class="elevation-1 px-4 mx-2 py-4 my-5 grey lighten-2"
        >
          <span v-if="optionList.length > 1">
            {{ $t('option.allOptionsAreInConflictWith') }}
            <option-chip :option="currentOption" />
          </span>
          <span v-else>
            {{ $t('option.noOtherOption') }}
          </span>
        </v-row>
      </template>
      <template v-if="!noMoreContradictoryOptions && askForMoreContradictoryOptions && !loadingOptions">
        <v-row
          v-if="!loadingOptions && !loadingConflictingOptions"
          no-gutters
          justify="start"
          class="elevation-1 px-4 mx-2 py-4 my-5 grey lighten-2"
        >
          <v-col cols="6">
            <h4
              class="pa-3"
              v-show="contradictoryOptions.length === 0"
            >
              {{ $t('option.isThereAContradictoryOptionTo') }}
              <option-chip
                :option="currentOption"
              />
              ?
            </h4>
            <h4
              class="pa-3"
              v-show="contradictoryOptions.length > 0"
            >
              {{ $t('option.isThereAnotherContradictoryOptionTo') }}
              <option-chip
                :option="currentOption"
              />
              ?
            </h4>
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="select"
              @change="addContradictoryOption($event)"
              class="d-inline rai-select__margion-top"
              :items="availableOptions"
              :item-text="showLogicalLanguage ? 'prolog' : 'name'"
              item-value="id"
              return-object
              :label="$t('global.select')"
              solo
              :no-data-text="$t('option.noMoreOption')"
              :loading="loadingConflictingCreation"
            >

            </v-select>
          </v-col>
        </v-row>
      </template>

    </v-container>

    <footer-navigation
      :prev-button-function="goToAddMoreOptionToAMinimalScenarii"
      prev-button-label="scenario.addMoreOptionsToAMinimalScenario"
      :next-button-function="goToIdentifyIncompatibleScenarii"
      next-button-label="scenario.identifyIncompatibleScenarii"
    />
  </div>
</template>

<script>
import PageContentToolbar from '@/components/layout/PageContentToolbar';
import FooterNavigation from '@/components/layout/FooterNavigation';
import OptionBlock from '@/components/global/OptionBlock';
import OptionChip from '@/components/global/OptionChip';

import projectPageMixin from '@/mixins/project-page-mixin.js';

import optionService from '@/services/options.js';

export default {
  name: 'IdentifyContradictoryOptions',

  components: { PageContentToolbar, FooterNavigation, OptionBlock, OptionChip },

  mixins: [ projectPageMixin ],

  data () {
    return {
      currentOption: {},
      contradictoryOptions: [],
      optionList: [],
      askForMoreContradictoryOptions: true,
      loadingOptions: false,
      loadingConflictingOptions: false,
      loadingConflictingCreation: false,
      select: 0,
    };
  },

  computed: {
    /*
    * all options except current and options that are already complement to current
    */
    availableOptions () {
      return this.optionList.filter(opt => {
        const found = this.computedContradictoryOptions.find(cont => cont.id === opt.id)
        return this.currentOption.id !== opt.id && !found
      });
    },

    /*
    * the actual contradictory options list, not complements entities
    */
    computedContradictoryOptions () {
      return this.contradictoryOptions
        .filter(comp => comp.option.id === this.currentOption.id || comp.optioncomplement.id === this.currentOption.id)
        .map(comp => {
          let option = {};
          if (comp.option.id === this.currentOption.id) {
            option = comp.optioncomplement;
          } else if (comp.optioncomplement.id === this.currentOption.id) {
            option = comp.option;
          }
          option.isCloseable = comp.isManual;
          return option;
        });
    },

    noMoreContradictoryOptions () {
      return this.availableOptions.length <= 0;
    },

    showLogicalLanguage () {
      return this.$store.state.showLogicalLangage;
    },
  },

  methods: {
    addContradictoryOption (option) {
      const conflictingOptions = {
        "option": this.currentOption,
        "optioncomplement": option,
      };
      this.loadingConflictingCreation = true;
      optionService.createConflictingOptions(this.$route.params.id, conflictingOptions)
        .then(res => {
          // this.init();
          // console.log(res.data)
          this.contradictoryOptions.push(res.data);
          this.select = 0;
          this.$emit('raison-event', { type: 'success', messages: [this.$t('option.contradictoryCreated')] });
        })
        .catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] });
        })
        .finally(() => {
          this.loadingConflictingCreation = false;
          if (this.availableOptions.length) {
            this.askForMoreContradictoryOptions = true;
          }
        })
    },

    removeContradictoryOption (option) {
      // console.log('removeContradictoryOption',option)
      const complementIdToDelete = [ this.currentOption.id, option.id ].sort().join('')
      const complementIndex = this.contradictoryOptions.findIndex(comp => comp.id === complementIdToDelete);
      const complementId = this.contradictoryOptions[complementIndex].id;
      // console.log(this.contradictoryOptions[complementIndex])
      const flag = this.contradictoryOptions[complementIndex].isManual;
      optionService.deleteConflictingOption(this.$route.params.id, complementId).then(() => {
        this.contradictoryOptions.splice(complementIndex, 1);
        this.$emit('raison-event', { type: 'success', messages: [this.$t('option.contradictoryDeleted')] });
      }).catch(err => {
        if(flag){
          this.$emit('raison-event', { type: 'error', messages: [this.$t('option.canDeletecontradictoryOption')] });
        }else{
          this.$emit('raison-event', { type: 'error', messages: [err] });
        }
      }).finally(() => {
        if (this.noMoreContradictoryOptions) {
          this.askForMoreContradictoryOptions = true;
        }
      })
    },

    goToAddMoreOptionToAMinimalScenarii() {
      this.$router.push({name: 'addOptionsToScenario', params: {id: this.$route.params.id, scenarioId: 'noId'}});
    },

    goToIdentifyIncompatibleScenarii () {
      this.$router.push({name: 'incompatibleScenarii', params: { id: this.$route.params.id } });
    },

    changeCurrentOption (event) {
      this.currentOption = event;
      this.askForMoreContradictoryOptions = true;
    },

    init() {
      this.loadingOptions = true;
      Promise.all([
        optionService.getOptions(this.$route.params.id),
        optionService.getAllConflictingOptions(this.$route.params.id),
      ])
      .then(responses => {
        this.optionList = responses[0].data.sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate));
        this.contradictoryOptions = responses[1].data;
        if (this.optionList.length > 0 && Object.keys(this.currentOption).length == 0 ) {
          this.currentOption = this.optionList[0];
        }
      })
      .catch(err => {
        this.$emit('raison-event', { type:'error', messages: [err] });
      })
      .finally(() => {
        this.loadingOptions = false;
      });
    }
  },

  created () {
    this.init();
  },
};
</script>

<style>
.rai-select__margion-top .v-input__control { /* vuetify's v-select do not allow setting helper class --' this is a workaround */
  margin-top: 10px
}
</style>